import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import type { MetricsByPlatform } from "@/components/PostCard";

export type PostType = "standard" | "reel" | "story";

export interface Post {
  id: string;
  clientId: string;
  postType: PostType;
  status: ValidStatuses;
  groupName: string | null;
  caption: string | null;
  hashtags: string | null;
  isCarousel: boolean;
  canBeInGrid: boolean;
  syncedToNotion?: boolean;
  notionPageId?: string | null;
  createdAt: string;
  updatedAt: string;
  scheduledAt?: string | null;
  publishedAt?: string | null;
  /** Set when the client clicks "Approva" on the review page. Drives
   * the dashboard's "needs review" predicate. Status-neutral — admin
   * owns the lifecycle. */
  clientReviewedAt?: string | null;
  /** Legacy field from the old auto-approve flow. Read-only history;
   * new code does not write to it. */
  clientRejectionReason?: string | null;
  mediaItems: MediaItem[];
  gridItems?: any[];
  notionSynced?: boolean;
  notion?: any;
  // Reel-specific fields
  thumbnailMediaId?: string;
  isReel?: boolean;
  hasValidReelContent?: boolean;
  thumbnailMediaItem?: MediaItem; // Populated by backend or fetched separately

  // Social media publishing fields
  postFolderPrepared?: boolean;
  postFolderPreparationStatus?: 'pending' | 'preparing' | 'ready' | 'failed';
  postFolderPreparationError?: string | null;
  lastPublishedAt?: string | null;

  // Active grid position (computed by backend)
  activeGridPosition?: number | null;
  activeGridTotal?: number | null;

  // Idea link
  ideaId?: string | null;

  // Notes
  notesCount?: number;

  // Public sharing
  publicStatus?: string | null;
  engagementScore?: number | null;

  // Ads / boost signals
  isCommercial?: boolean;
  boostPriority?: number | null;

  // Lifetime social-media metrics per platform. Populated by the
  // backend only on published/partially_published posts; null on
  // anything still in draft. See PublishedPostStats for the shape.
  metricsByPlatform?: MetricsByPlatform | null;
}

export interface CreatePostImage {
  mediaItemId?: number;
  originalId?: string;
  originalFilename?: string;
  position?: number;
  folderInfo?: {
    folderId: string;
    folderName: string;
  };
  // Optional manual 4:5 crop, together with the pixel space it was
  // measured in (the crop UI displays a downscaled variant; the backend
  // rescales onto the original at prep time). When omitted the backend
  // falls back to its default centered crop during post preparation.
  cropRect?: {
    x: number;
    y: number;
    width: number;
    height: number;
    sourceWidth: number;
    sourceHeight: number;
  };
}

export interface CreatePostRequest {
  postId: string;
  groupName: string | null;
  postType?: "standard" | "reel" | "story";
  caption: string | null;
  hashtags: string | null;
  images?: CreatePostImage[];
}

export interface CreatePostResponse {
  success: boolean;
  message: string;
  post: Post;
  created: boolean;
  mediaItemsCount: number;
}

export interface UpdatePostRequest {
  postId: string;
  groupName?: string;
  postType?: "standard" | "reel" | "story";
  caption?: string;
  hashtags?: string;
  images?: CreatePostImage[];
  status?: string;
  // Ads candidate flag. When true, the auto-boost flow considers the
  // post for campaign suggestions; when false/null, it's ignored.
  isCommercial?: boolean;
  // Optional ranking for auto-boost prioritization when multiple
  // commercial candidates compete for the same budget.
  boostPriority?: number | null;
}

export interface UpdatePostResponse {
  success: boolean;
  message: string;
  post: Post;
  updatedFields: string[];
}

export interface DeletePostResponse {
  success: boolean;
  message: string;
  details?: any;
}

export enum PostStatus {
  // Pre-draft: AI-generated posts not yet promoted to normal workflow
  AI_GENERATED = "ai_generated",
  // Creation statuses
  DRAFT = "draft",
  CAPTION_GENERATED = "caption_generated",
  // Approval status (single — admin owns the lifecycle, client review
  // is captured separately on `client_reviewed_at`)
  APPROVAL_PENDING = "approval_pending",
  // Ready for scheduling
  READY = "ready",
  SCHEDULED = "scheduled",
  // Publishing outcomes
  PARTIALLY_PUBLISHED = "partially_published",
  PUBLISHED = "published",
  ARCHIVED = "archived",
}

export const VALID_STATUSES = [
  PostStatus.AI_GENERATED,
  PostStatus.DRAFT,
  PostStatus.CAPTION_GENERATED,
  PostStatus.APPROVAL_PENDING,
  PostStatus.READY,
  PostStatus.SCHEDULED,
  PostStatus.PARTIALLY_PUBLISHED,
  PostStatus.PUBLISHED,
  PostStatus.ARCHIVED,
] as const;

export type ValidStatuses = PostStatus;

// Legacy type alias for backward compatibility
export type PostStatusType = PostStatus;

export interface StatusUpdateResponse {
  success: boolean;
  message: string;
  oldStatus: string;
  newStatus: string;
  post: Post;
}

export interface StatusStatisticsResponse {
  statusStatistics: Record<PostStatus, number>;
  total: number;
}

// Media Item Stages - simpler than post statuses
export enum MediaItemStage {
  ARCHIVED = "archived",
  READY = "ready",
  NEXT = "next",
  PUBLISHED = "published",
}

export const VALID_MEDIA_STAGES = [
  MediaItemStage.ARCHIVED,
  MediaItemStage.READY,
  MediaItemStage.NEXT,
  MediaItemStage.PUBLISHED,
] as const;

export type MediaStage = MediaItemStage;
